import { Typography as MuiTypography } from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { z } from "zod";
import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import StreamflowChartContainer from "../../components/charts/streamflowChart/StreamflowChartContainer";
import { getWaterYearRange } from "../fieldDepthProfile/FieldDepthProfile";
import React, { useMemo, useEffect } from "react";
import { usePostHeight } from "../../hooks/usePostHeight";
import useFetchGraphData from "../../hooks/useFetchGraphData";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);

const { start_date, end_date } = getWaterYearRange();

const PARAMETER_OPTIONS = [
  { label: "Flow", value: "2" },
  { label: "Stage", value: "1901" },
];

const DEFAULTS = {
  parameterid: 2,
  start_date,
  end_date,
  locationid: [],
};

const schema = z.object({
  parameterid: z.coerce.number().default(DEFAULTS.parameterid),
  start_date: z.string().default(DEFAULTS.start_date),
  end_date: z.string().default(DEFAULTS.end_date),
  locationid: z
    .preprocess((v) => {
      if (Array.isArray(v)) return v;
      if (typeof v === "string" && v.length > 0) return v.split(",");
      return [];
    }, z.array(z.string()))
    .default(DEFAULTS.locationid),
  controls: z.preprocess(
    (val) => val === "true" || val === true,
    z.boolean().default(false)
  ),
  height: z.preprocess(
    (val) => (typeof val === "string" ? parseInt(val, 10) : val),
    z.number().default(400)
  ),
});

export default function StreamflowDataEmbed() {
  usePostHeight();
  const { params, setParams } = useQueryParams(schema);

  const { data: locationsData } = useFetchGraphData(
    {},
    "streamflow-data/locations"
  );

  const locationOptions = useMemo(() => {
    if (!locationsData?.length) return [];
    return locationsData.map((loc) => ({
      label: loc.location_name,
      value: String(loc.locationid),
    }));
  }, [locationsData]);

  useEffect(() => {
    if (params.locationid.length === 0 && locationOptions.length > 0) {
      setParams((prev) => ({
        ...prev,
        locationid: locationOptions.map((loc) => loc.value),
      }));
    }
  }, [locationOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const locationIdString = params.locationid.join(",");

  const FILTERS = useMemo(
    () => [
      {
        type: "select",
        param: "parameterid",
        label: "Parameter",
        default: String(params.parameterid),
        options: PARAMETER_OPTIONS,
      },
      {
        type: "date",
        param: "start_date",
        label: "Start Date",
        default: params.start_date,
      },
      {
        type: "date",
        param: "end_date",
        label: "End Date",
        default: params.end_date,
      },
      {
        type: "auto",
        param: "locationid",
        label: "Locations",
        multi: true,
        default: params.locationid,
        options: locationOptions,
      },
    ],
    [params, locationOptions]
  );

  return (
    <>
      <main>
        {params.controls && (
          <Section>
            <Typography variant="h5" component="h2" gutterBottom>
              Filters
            </Typography>
            <FiltersForm config={FILTERS} onChange={setParams} />
          </Section>
        )}

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Streamflow Data
          </Typography>
          <StreamflowChartContainer
            parameterid={params.parameterid}
            locationid={locationIdString}
            startDate={params.start_date}
            endDate={params.end_date}
            height={params.height}
          />
        </Section>
      </main>
    </>
  );
}
